import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import { ALL_BRANDS_QUERY } from '@/api/handlers/resources/brands/requests';
import { BrandQueryKeys } from '@/api/queries/brands/brandQueryKeys';
import { CategoryQueryKeys } from '@/api/queries/categories/categoryQueryKeys';
import { ProductQueryKeys } from '@/api/queries/products/productQueryKeys';
import { SupplierQueryKeys } from '@/api/queries/suppliers/supplierQueryKeys';
import { flattenPickerCategories } from '@/pages/products/components/productsMeta';
import { PATH } from '@/router/PATH';
import { matchBarcodes, type BarcodeFetchers } from './barcodeMatcher';
import {
    buildAnalysisResult,
    buildLabelToKey,
    byName,
    electSupplier,
    ExcelImportError,
    type ParseErrorKind,
} from './excelImportMeta';
import { parseProductsSheet } from './excelParse';
import { setPendingImport } from './pendingFileImport';

/** Localized-copy selector: `purchases.excelImport.errors.${key}`. */
export type ExcelImportErrorKey =
    ParseErrorKind | 'invalidType' | 'analysisFailed';

export type ExcelImportStage =
    | { kind: 'select' }
    | { kind: 'analyzing'; fileName: string }
    | { kind: 'error'; errorKey: ExcelImportErrorKey };

export type UseExcelImportArgs = {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
};

const XLSX_MIME =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Sanity cap for candidate paging — 5 × 200 products per barcode. */
const MAX_BARCODE_LOOKUP_PAGES = 5;

const isXlsxFile = (file: File): boolean =>
    file.type === XLSX_MIME || file.name.toLowerCase().endsWith('.xlsx');

/**
 * Drives the Excel-import modal: file intake (drop / select), the analysis
 * pipeline (parse sheet → match barcodes → resolve names → build the draft)
 * and the handoff to /purchases/file-import via the one-shot pending holder.
 *
 * Closing the modal mid-analysis abandons the in-flight run (an analysis-id
 * guard keeps a finished abandoned run from navigating or writing the
 * holder). exceljs only loads inside the parser, on first use.
 */
export const useExcelImport = ({
    isOpen,
    onOpenChange,
}: UseExcelImportArgs) => {
    const { i18n } = useTranslation('pages');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { apiClient } = useApi();

    const [stage, setStage] = React.useState<ExcelImportStage>({
        kind: 'select',
    });
    const analysisIdRef = React.useRef(0);

    // Each open starts fresh; closing also invalidates any in-flight analysis.
    React.useEffect(() => {
        if (!isOpen) {
            analysisIdRef.current += 1;
            setStage({ kind: 'select' });
        }
    }, [isOpen]);

    // Header labels must match a template generated in EITHER locale,
    // regardless of the active UI language.
    const labelToKey = React.useMemo(
        () =>
            buildLabelToKey([
                i18n.getFixedT('en', 'pages'),
                i18n.getFixedT('ka', 'pages'),
            ]),
        [i18n],
    );

    const barcodeFetchers = React.useMemo<BarcodeFetchers>(
        () => ({
            // The server-side `barcode` filter is a substring match, so the
            // exact-matching variant can sit on any page — walk them all.
            // A barcode matching more than MAX_BARCODE_LOOKUP_PAGES × 200
            // products is garbage input; throwing marks the row doubtful
            // (lookupFailed) instead of mis-importing a restock as new.
            findProductIdsByBarcode: async (barcode) => {
                const ids: string[] = [];
                let page = 1;
                let totalPages: number;
                do {
                    const query = { page, pageSize: 200, barcode };
                    const result = await queryClient.ensureQueryData({
                        queryKey: ProductQueryKeys.list(query),
                        queryFn: () => apiClient.products.getProductList(query),
                    });
                    if (!result.success) {
                        throw new Error('product list failed');
                    }
                    ids.push(...result.response.items.map((item) => item.id));
                    totalPages = result.response.totalPages;
                    if (totalPages > MAX_BARCODE_LOOKUP_PAGES) {
                        throw new Error('barcode lookup result too broad');
                    }
                    page += 1;
                } while (page <= totalPages);
                return ids;
            },
            getProductDetail: async (productId) => {
                const result = await queryClient.ensureQueryData({
                    queryKey: ProductQueryKeys.detail(productId),
                    queryFn: () => apiClient.products.getProduct(productId),
                });
                if (!result.success) throw new Error('product detail failed');
                return result.response;
            },
        }),
        [apiClient, queryClient],
    );

    const fetchNameMaps = React.useCallback(async () => {
        const [suppliers, categories, brands] = await Promise.all([
            queryClient.ensureQueryData({
                queryKey: SupplierQueryKeys.picker(),
                queryFn: () => apiClient.suppliers.getSupplierPicker(),
            }),
            queryClient.ensureQueryData({
                queryKey: CategoryQueryKeys.picker(),
                queryFn: () => apiClient.categories.getCategoryPicker(),
            }),
            queryClient.ensureQueryData({
                queryKey: BrandQueryKeys.listPage(ALL_BRANDS_QUERY),
                queryFn: () => apiClient.brands.getBrandList(ALL_BRANDS_QUERY),
            }),
        ]);
        return {
            suppliersByName: byName(
                suppliers.success ? suppliers.response.items : [],
            ),
            categoriesByName: byName(
                categories.success
                    ? flattenPickerCategories(categories.response.items)
                    : [],
            ),
            brandsByName: byName(brands.success ? brands.response.items : []),
        };
    }, [apiClient, queryClient]);

    const analyzeFile = React.useCallback(
        async (file: File) => {
            if (!isXlsxFile(file)) {
                setStage({ kind: 'error', errorKey: 'invalidType' });
                return;
            }
            const analysisId = ++analysisIdRef.current;
            const isStale = () => analysisIdRef.current !== analysisId;
            setStage({ kind: 'analyzing', fileName: file.name });
            try {
                const buffer = await file.arrayBuffer();
                const rows = await parseProductsSheet(buffer, labelToKey);

                const [matches, nameMaps] = await Promise.all([
                    matchBarcodes(
                        rows.map((row) => row.cells.barcode ?? ''),
                        barcodeFetchers,
                    ),
                    fetchNameMaps(),
                ]);
                if (isStale()) return;

                const result = buildAnalysisResult(
                    rows,
                    {
                        ...nameMaps,
                        supplierId: electSupplier(
                            rows,
                            nameMaps.suppliersByName,
                        ),
                        barcodeMatch: matches,
                    },
                    file.name,
                );
                setPendingImport(result);
                onOpenChange(false);
                navigate(PATH.PURCHASES.FILE_IMPORT);
            } catch (error) {
                if (isStale()) return;
                setStage({
                    kind: 'error',
                    errorKey:
                        error instanceof ExcelImportError
                            ? error.kind
                            : 'analysisFailed',
                });
            }
        },
        [barcodeFetchers, fetchNameMaps, labelToKey, navigate, onOpenChange],
    );

    const onFilesPicked = React.useCallback(
        (files: ReadonlyArray<File>) => {
            const file = files[0];
            if (file) void analyzeFile(file);
        },
        [analyzeFile],
    );

    // Drag payloads expose files lazily via `getFile()`; resolve them, then
    // hand off to the same intake path as the file picker.
    const onDrop = React.useCallback(
        async (event: {
            items: Array<{ kind: string; getFile?: () => Promise<File> }>;
        }) => {
            const dropped: File[] = [];
            for (const item of event.items) {
                if (item.kind === 'file' && item.getFile) {
                    dropped.push(await item.getFile());
                }
            }
            if (dropped.length > 0) {
                onFilesPicked(dropped);
            }
        },
        [onFilesPicked],
    );

    const onTryAgain = React.useCallback(
        () => setStage({ kind: 'select' }),
        [],
    );

    return { stage, onDrop, onFilesPicked, onTryAgain } as const;
};
